import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Wallet, CreditCard, IndianRupee, TrendingUp, TrendingDown,
  Plus, Calendar, FileText, RefreshCw, Building2, History,
  CheckCircle, AlertTriangle, Clock
} from 'lucide-react';

const paymentModes = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'rtgs', label: 'RTGS' },
  { value: 'neft', label: 'NEFT' },
];

export default function HQPayments() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState(null);
  const [entries, setEntries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'bank_transfer',
    amount: '',
    bank_name: '',
    cheque_number: '',
    cheque_date: '',
    transaction_reference: '',
    notes: '',
  });

  const fetchLedgerData = useCallback(async () => {
    try {
      setLoading(true);
      const [ledgerRes, entriesRes, paymentsRes, invoicesRes] = await Promise.all([
        api.get('/api/ledgers/franchise/my-ledger'),
        api.get('/api/ledgers/franchise/my-ledger/entries?page_size=20'),
        api.get('/api/ledgers/franchise/my-payments?page_size=20'),
        api.get('/api/ledgers/franchise/my-outstanding-invoices?page_size=50'),
      ]);
      
      setLedger(ledgerRes.data);
      setEntries(entriesRes.data.items || []);
      setPayments(paymentsRes.data.items || []);
      setOutstandingInvoices(invoicesRes.data.items || []);
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      if (error.response?.status === 403) {
        toast.error('This feature is only available for franchise stores');
      } else {
        toast.error('Failed to load ledger data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLedgerData();
  }, [fetchLedgerData]);

  const handlePaymentSubmit = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!paymentForm.payment_mode) {
      toast.error('Please select a payment mode');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/api/ledgers/franchise/my-payments', {
        franchise_store_id: user.entity_id,
        payment_date: paymentForm.payment_date,
        payment_mode: paymentForm.payment_mode,
        amount: parseFloat(paymentForm.amount),
        bank_name: paymentForm.bank_name || null,
        cheque_number: paymentForm.cheque_number || null,
        cheque_date: paymentForm.cheque_date || null,
        transaction_reference: paymentForm.transaction_reference || null,
        notes: paymentForm.notes || null,
      });
      
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      resetPaymentForm();
      fetchLedgerData();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'bank_transfer',
      amount: '',
      bank_name: '',
      cheque_number: '',
      cheque_date: '',
      transaction_reference: '',
      notes: '',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getPaymentModeLabel = (mode) => {
    const found = paymentModes.find(m => m.value === mode);
    return found ? found.label : mode;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Check if user is franchise
  if (user?.store_type !== 'franchise') {
    return (
      <div className="p-6">
        <Card className="max-w-lg mx-auto">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Feature Not Available</h3>
            <p className="text-gray-600">
              HQ Payments is only available for franchise stores. Company-owned stores do not maintain a separate ledger with HQ.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="hq-payments-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HQ Account</h1>
          <p className="text-gray-600">Manage your outstanding balance and payments to HQ</p>
        </div>
        <Button onClick={() => setShowPaymentDialog(true)} data-testid="record-payment-btn">
          <Plus className="w-4 h-4 mr-2" />
          Record Payment
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Outstanding Balance</p>
                <p className={`text-2xl font-bold ${(ledger?.current_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(ledger?.current_balance)}
                </p>
              </div>
              <div className={`p-3 rounded-full ${(ledger?.current_balance || 0) > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                <Wallet className={`w-6 h-6 ${(ledger?.current_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Credit Limit</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(ledger?.credit_limit)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Purchases</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(ledger?.total_invoiced || ledger?.total_purchases)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(ledger?.total_paid)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-emerald-100">
                <TrendingDown className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Credit Status Alert */}
      {ledger?.credit_limit > 0 && ledger?.current_balance > 0 && (
        <Card className={`border-l-4 ${
          ledger.current_balance >= ledger.credit_limit 
            ? 'border-l-red-500 bg-red-50'
            : ledger.current_balance >= ledger.credit_limit * 0.8
              ? 'border-l-yellow-500 bg-yellow-50'
              : 'border-l-green-500 bg-green-50'
        }`}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              {ledger.current_balance >= ledger.credit_limit ? (
                <>
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800">
                    Credit limit exceeded! Your outstanding balance exceeds the credit limit by {formatCurrency(ledger.current_balance - ledger.credit_limit)}.
                  </span>
                </>
              ) : ledger.current_balance >= ledger.credit_limit * 0.8 ? (
                <>
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <span className="text-yellow-800">
                    Credit utilization at {Math.round((ledger.current_balance / ledger.credit_limit) * 100)}%. Consider making a payment soon.
                  </span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800">
                    Available credit: {formatCurrency(ledger.credit_limit - ledger.current_balance)}
                  </span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <FileText className="w-4 h-4 mr-2" />
            Statement
          </TabsTrigger>
          <TabsTrigger value="payments">
            <History className="w-4 h-4 mr-2" />
            Payment History
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <Building2 className="w-4 h-4 mr-2" />
            Outstanding Invoices
          </TabsTrigger>
        </TabsList>

        {/* Statement Tab */}
        <TabsContent value="overview" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Account Statement</CardTitle>
              <CardDescription>Recent transactions with HQ</CardDescription>
            </CardHeader>
            <CardContent>
              {entries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No transactions found
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{formatDate(entry.entry_date)}</TableCell>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{entry.reference_number || '-'}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {entry.debit_amount > 0 ? formatCurrency(entry.debit_amount) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {entry.credit_amount > 0 ? formatCurrency(entry.credit_amount) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(entry.running_balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="payments" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
              <CardDescription>Payments made to HQ</CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No payments recorded yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Payment #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="font-medium">{payment.payment_number}</TableCell>
                        <TableCell>{formatDate(payment.payment_date)}</TableCell>
                        <TableCell>{getPaymentModeLabel(payment.payment_mode)}</TableCell>
                        <TableCell>{payment.transaction_reference || payment.cheque_number || '-'}</TableCell>
                        <TableCell className="text-right font-medium text-emerald-600">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800">
                            {payment.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outstanding Invoices Tab */}
        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Outstanding Invoices</CardTitle>
              <CardDescription>Transfer invoices pending payment</CardDescription>
            </CardHeader>
            <CardContent>
              {outstandingInvoices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No outstanding invoices
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Total Amount</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Outstanding</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {outstandingInvoices.map((invoice) => (
                      <TableRow key={invoice.invoice_id}>
                        <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                        <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(invoice.total_amount)}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(invoice.paid_amount)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          {formatCurrency(invoice.outstanding_amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Record Payment to HQ</DialogTitle>
            <DialogDescription>
              Record a payment made against your outstanding balance with HQ.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Balance Info */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Current Outstanding</span>
                <span className="font-bold text-lg text-red-600">
                  {formatCurrency(ledger?.current_balance)}
                </span>
              </div>
            </div>

            {/* Payment Date */}
            <div className="space-y-2">
              <Label htmlFor="payment_date">Payment Date *</Label>
              <Input
                id="payment_date"
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
              />
            </div>

            {/* Payment Mode */}
            <div className="space-y-2">
              <Label htmlFor="payment_mode">Payment Mode *</Label>
              <Select
                value={paymentForm.payment_mode}
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_mode: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment mode" />
                </SelectTrigger>
                <SelectContent>
                  {paymentModes.map((mode) => (
                    <SelectItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="amount"
                  type="number"
                  placeholder="0.00"
                  className="pl-9"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                />
              </div>
            </div>

            {/* Conditional fields based on payment mode */}
            {['bank_transfer', 'rtgs', 'neft'].includes(paymentForm.payment_mode) && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    placeholder="Enter bank name"
                    value={paymentForm.bank_name}
                    onChange={(e) => setPaymentForm({ ...paymentForm, bank_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transaction_reference">Transaction Reference / UTR</Label>
                  <Input
                    id="transaction_reference"
                    placeholder="Enter reference number"
                    value={paymentForm.transaction_reference}
                    onChange={(e) => setPaymentForm({ ...paymentForm, transaction_reference: e.target.value })}
                  />
                </div>
              </>
            )}

            {paymentForm.payment_mode === 'cheque' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="cheque_number">Cheque Number</Label>
                  <Input
                    id="cheque_number"
                    placeholder="Enter cheque number"
                    value={paymentForm.cheque_number}
                    onChange={(e) => setPaymentForm({ ...paymentForm, cheque_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cheque_date">Cheque Date</Label>
                  <Input
                    id="cheque_date"
                    type="date"
                    value={paymentForm.cheque_date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, cheque_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    placeholder="Enter bank name"
                    value={paymentForm.bank_name}
                    onChange={(e) => setPaymentForm({ ...paymentForm, bank_name: e.target.value })}
                  />
                </div>
              </>
            )}

            {paymentForm.payment_mode === 'upi' && (
              <div className="space-y-2">
                <Label htmlFor="transaction_reference">UPI Reference</Label>
                <Input
                  id="transaction_reference"
                  placeholder="Enter UPI transaction ID"
                  value={paymentForm.transaction_reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transaction_reference: e.target.value })}
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes (optional)"
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handlePaymentSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Recording...
                </>
              ) : (
                'Record Payment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
